import { makeStyles } from '@material-ui/core';
import { KeyboardArrowRight, Refresh } from '@material-ui/icons';
import { AnimatePresence, AnimateSharedLayout, motion } from 'framer-motion';
import React from 'react';
import ReactDOM from 'react-dom';
import { Save } from 'react-feather';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { actions, useTypedDispatch, useTypedSelector } from '../../../redux';
import { theme } from '../../../theme/theme';
import { motions, ProgressTracker } from '../../global';
import { ProjectScheduler, ProjectSchedulerTracker } from '../../project/scheduler/ProjectScheduler';
import { AllSetPane } from './AllSetPane';
import { SectionCard, SectionCardMenu, SectionCardTitle } from './cards/SectionCard';
import { SummaryCard } from './cards/SummaryCard';
import * as assetData from './sections/AssetData';
import * as damageMechanisms from './sections/DamageMechanisms';
import * as imageDesign from './sections/ImageDesign';
import * as inspectionTasks from './sections/InspectionTasks';
import * as inspectionTypes from './sections/InspectionTypes';
import * as references from './sections/References';
import * as riskAssessment from './sections/RiskAssessment';
import * as thickness from './sections/Thickness';

const mod = 'InspectionCreationWizard';

/**
 * Stages of the inspection plan creation process.
 */
export type Stage = 'prepare' | 'review' | 'publish' | 'schedule' | 'completed';

/**
 * State associated to each section of the inspection creation.
 */
export type SectionState = { display: boolean; editable: boolean; verified: boolean; step: number; steps: number };
export const newState = (display: boolean): SectionState => ({
    display,
    editable: true,
    verified: false,
    step: 0,
    steps: 1,
});

/**
 * Section modules and objects.
 */
const sectionModules = Object.freeze([
    assetData,
    imageDesign,
    thickness,
    damageMechanisms,
    inspectionTypes,
    inspectionTasks,
    riskAssessment,
    references,
]);

const sections = Object.freeze(sectionModules.map(m => ({ ...m })));

const useInspectionCreationWizardClasses = makeStyles({
    root: {
        display: 'flex',
        flex: 1,
        padding: 30,
        background: theme.colors.neutral[6],
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
    },
});

/**
 * The inspection creation wizard component.
 *
 * This component manages the progress of the inspection creation.
 */
export const InspectionCreationWizard = () => {
    const classes = useInspectionCreationWizardClasses();
    const navigate = useNavigate();
    const dispatch = useTypedDispatch();
    const { asset } = useTypedSelector(state => ({ asset: state.asset }));
    const stages = ['prepare', 'review', 'publish', 'schedule', 'completed'] as Stage[];
    const [stage, nextStage] = React.useReducer(
        (state: Stage) => stages[Math.min(stages.indexOf(state) + 1, stages.length - 1)],
        stages[0],
    );
    const [index, setIndex] = React.useState(0);

    return (
        <div className={classes.root}>
            <AnimatePresence>
                <Summary stage={stage} index={index} />
            </AnimatePresence>
            <div className={classes.content}>
                <AnimatePresence>
                    {stage === 'prepare' || stage === 'review' ? (
                        <PlanCreationSections
                            stage={stage}
                            nextIndex={setIndex}
                            onPrepareComplete={nextStage}
                            onReviewComplete={async () => {
                                if (stage === 'review')
                                    await Promise.all([
                                        dispatch(actions.asset.update()),
                                        dispatch(actions.plan.update()),
                                    ]);
                                nextStage();
                            }}
                        />
                    ) : stage === 'publish' && (asset.fetching || asset.error) ? (
                        <span>{`${asset.status} ${asset.error?.message}`}</span>
                    ) : stage === 'publish' ? (
                        <AllSetPane
                            onSchedule={() => {
                                dispatch(actions.project.create());
                                nextStage();
                            }}
                            onBack={() => navigate('/')}
                        />
                    ) : (
                        <WizardScheduler />
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

const useSummaryClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        width: 260,
        margin: '-20px 30px 0 0',
    },
    item: {
        display: 'flex',
        flexDirection: 'column',
        margin: '20px 0',
    },
});

/**
 * All summary elements of the inspection creation wizard.
 *
 * @param props.stage stage of the plan creation process
 * @param props.index current section index
 */
const Summary = (props: { stage: Stage; index: number }) => {
    const classes = useSummaryClasses();
    const { t } = useTranslation(['inspection-creation']);
    const items = sections.map(s => t(s.label));
    const summaries = {
        prepare: props.stage === 'prepare' ? 'doing' : 'done',
        review: props.stage === 'review' ? 'doing' : props.stage !== 'prepare' ? 'done' : 'todo',
        schedule: props.stage === 'schedule' ? 'doing' : props.stage === 'completed' ? 'done' : 'todo',
    };

    return (
        <motion.div className={classes.root} {...motions.presence({ slide: 10 })}>
            {Object.entries(summaries).map(([name, completion]) => (
                <div key={name} className={classes.item}>
                    <SummaryCard
                        focused={completion === 'doing'}
                        message={t(`${mod}.summary-${name}-${completion}-status`)}
                        title={t(`${mod}.summary-${name}-${completion}-title`)}
                        subtitle={t(`${mod}.summary-${name}-${completion}-subtitle`)}
                        items={name === 'prepare' ? items : []}
                        progress={name === 'prepare' ? props.index : completion === 'done' ? 0 : -1}
                    />
                </div>
            ))}
        </motion.div>
    );
};

const useSectionsClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        margin: '-5px 0',
    },
    section: {
        display: 'flex',
        flexDirection: 'column',
        margin: '5px 0',
    },
    tracker: {
        position: 'sticky',
        bottom: 30,
        display: 'flex',
        flexDirection: 'column',
        zIndex: 2,
    },
});

/**
 * Sections of the inspection plan creation process.
 *
 * This component can be used standalone if `props.stage` is not provided and asset and plan stores are populated.
 * It will default to a view equivalent to `props.stage ==== 'review'` but without verification requirements.
 * The progress tracker will also not be rendered.
 *
 * @param props.stage stage of the plan creation process, sections are only displayed if state is 'prepare' or 'review'
 * @param props.nextIndex callback called each time a section is finished, the function argument is the section index
 * @param props.nextStage callback called when sections advance to the next stage, if `props.stage` is not provided,
 *        the callback is never called, if the callback is not provided, sections will stuck in the current stage
 */
export const PlanCreationSections = (props: {
    stage?: Stage;
    nextIndex?: (index: number) => void;
    onPrepareComplete?: () => void;
    onReviewComplete?: () => void;
}) => {
    const classes = useSectionsClasses();
    const { t } = useTranslation(['inspection-creation']);
    const dispatch = useTypedDispatch();
    const { asset, plan } = useTypedSelector(state => ({ asset: state.asset, plan: state.plan }));
    const forceUpdate = React.useReducer(() => ({}), {})[1];
    const sectionsState = React.useRef(sections.map((_, i) => newState(props.stage != undefined && i === 0)));
    const sectionsRef$ = React.useRef<HTMLDivElement[]>([...Array(sections.length)]);
    const [index, nextIndex] = React.useReducer(
        (state: number, next: number | undefined) => Math.min(next ?? state + 1, sections.length),
        props.stage === 'prepare' ? 0 : sections.length,
    );

    React.useLayoutEffect(() => {
        if (props.stage !== 'prepare') return;
        dispatch(actions.asset.reset());
        dispatch(actions.plan.reset());
    }, []);

    /**
     * Test if the next action is available.
     */
    const unlocked = () =>
        (props.stage === 'prepare' && !!asset.asset && !!plan.plan) ||
        (props.stage === 'review' && sectionsState.current.every(s => s.verified!));

    /**
     * Advance to the next section if the `locked` condition allows it, and call `props.nextIndex` and `props.nextStage`
     * callbacks if provided.
     */
    const next = () => {
        if (!unlocked()) return;
        dispatch(actions.asset.sharedCommit());
        const state = sectionsState.current[index];
        if (index < sections.length && state.step < state.steps - 1) {
            state.step++;
            forceUpdate();
            return;
        }
        sectionsState.current.forEach(s => (s.display = false));
        if (index < sectionsState.current.length - 1) sectionsState.current[index + 1].display = true;
        props.nextIndex?.(index + 1);
        nextIndex(undefined);
        if (index + 1 < sections.length) return;
        if (props.stage === 'prepare') {
            sectionsState.current.forEach(s => (s.verified = false));
            sectionsState.current[0].display = true;
            sectionsState.current[0].editable = false;
            props.onPrepareComplete?.();
        } else if (props.stage === 'review') {
            props.onReviewComplete?.();
        }
    };

    if (props.stage != undefined && props.stage !== 'prepare' && props.stage !== 'review') return <></>;

    return (
        <motion.div className={classes.root} {...motions.presence({ slide: 10 })}>
            <AnimateSharedLayout>
                <AnimatePresence>
                    {props.stage === 'review' && (
                        <motion.div className={classes.section} layout {...motions.presence({ slide: 10 })}>
                            <SectionCard
                                focused={true}
                                display={false}
                                header={
                                    <SectionCardTitle
                                        focused={true}
                                        title={t(`${mod}.review-title`)}
                                        subtitle={t(`${mod}.review-subtitle`)}
                                    />
                                }
                                children={''}
                            />
                        </motion.div>
                    )}
                    {Object.values(sections)
                        .slice(0, index + 1)
                        .map((section, i) => {
                            const { header, Component, steps } = section;
                            const state = sectionsState.current[i];
                            state.steps = steps;
                            if (props.stage == undefined) state.step = state.steps;
                            const focused = i === index;
                            const { title, subtitle } = header(t as any, state, focused, asset.changed, plan.changed);
                            const headerTitle = (
                                <SectionCardTitle focused={focused} title={title} subtitle={subtitle} />
                            );
                            const headerMenu = (
                                <SectionCardMenu
                                    display={state.display}
                                    editable={state.editable}
                                    verified={state.verified}
                                    onDisplay={display => ((state.display = display), forceUpdate())}
                                    onEditable={editable => ((state.editable = editable), forceUpdate())}
                                    onVerified={verified => {
                                        state.verified = verified;
                                        forceUpdate();
                                        if (!verified) return;
                                        dispatch(actions.asset.sharedCommit());
                                    }}
                                    showBadge={props.stage === 'review' && !state.display}
                                    showVerify={props.stage === 'review'}
                                />
                            );
                            return (
                                <motion.div
                                    key={i}
                                    ref={r => (sectionsRef$.current[i] = r!)}
                                    className={classes.section}
                                    layout
                                    {...motions.presence({ slide: 10 })}
                                >
                                    <SectionCard
                                        focused={focused}
                                        display={state.display}
                                        headerKey={title}
                                        childrenKey={`${state.editable}-${state.step}`}
                                        header={
                                            <>
                                                {headerTitle}
                                                {!focused && headerMenu}
                                            </>
                                        }
                                    >
                                        <Component state={state} next={next} />
                                    </SectionCard>
                                </motion.div>
                            );
                        })}
                </AnimatePresence>
            </AnimateSharedLayout>
            {props.stage != undefined && (
                <>
                    <div style={{ flex: 1, minHeight: 30 }} />
                    <div className={classes.tracker}>
                        <WizardTracker
                            stage={props.stage}
                            sectionData={sectionsState.current}
                            index={index}
                            unlocked={unlocked}
                            next={next}
                        />
                    </div>
                </>
            )}
        </motion.div>
    );
};

/**
 * Bar to track wizard progress based on the global progress tracker.
 *
 * @param props.stage current wizard stage
 * @param props.sectionData information about workflow sections
 * @param props.index current section index
 * @param props.unlocked function that checks if the `props.next` function can be called
 * @param props.next function to advance to the next step
 */
const WizardTracker = (props: {
    stage: Stage;
    sectionData: SectionState[];
    index: number;
    unlocked: () => boolean;
    next: () => void;
}) => {
    const { t } = useTranslation(['inspection-creation']);
    const dispatch = useTypedDispatch();
    const { asset, plan } = useTypedSelector(state => ({ asset: state.asset, plan: state.plan }));
    const start = props.index === 0 && props.sectionData[0].step === 0;
    const completed = Math.round(
        props.stage === 'prepare'
            ? (props.index / props.sectionData.length) * 80
            : (props.sectionData.reduce((acc, d) => acc + +d.verified, 0) / props.sectionData.length) * 20 + 80,
    );
    const estimated = Math.round(10 - completed / 10);
    const changed = asset.changes.length > 0 || plan.changes.length > 0;

    return (
        <ProgressTracker
            title={t(`${mod}.tracker-title-${start ? 'start' : 'complete'}`, { completed })}
            subtitle={t(`${mod}.tracker-subtitle-${completed < 100 ? 'estimate' : 'publish'}`, { count: estimated })}
            completed={completed}
            actions={[
                {
                    hidden: !changed,
                    title: t(`${mod}.tracker-icon-discard`),
                    icon: <Refresh />,
                    onClick: () => dispatch(actions.asset.sharedDiscard()),
                },
                {
                    hidden: start || !asset.asset || !plan.plan,
                    title: t(`${mod}.tracker-icon-save`),
                    icon: <Save />,
                    onClick: () => {}, // TODO
                },
            ]}
            button={{
                hidden: !props.unlocked(),
                text: t(`${mod}.tracker-button-${props.stage === 'review' ? 'publish' : 'next'}`),
                icon: <KeyboardArrowRight />,
                onClick: props.next,
            }}
        />
    );
};

const useWizardScheduleClasses = makeStyles({
    root: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
    },
    portalParent: {
        display: 'flex',
        flex: 1,
    },
    schedulerParent: {
        display: 'flex',
        flex: 1,
    },
    tracker: {
        position: 'sticky',
        bottom: 30,
    },
});

/**
 * Render the project scheduler component wrapper in a section card.
 * Since the scheduler complete page is not wrapped by the section card, the scheduler parent structure has to change
 * when the schedule completes.
 * This causes the scheduler to unmount and remount, losing its state.
 * To avoid that, a portal is created to new dom element, where the scheduler is rendered inside.
 * After that, this new element element is assigned as child of the expected scheduler target.
 */
const WizardScheduler = () => {
    const classes = useWizardScheduleClasses();
    const schedulerParent$ = React.useRef(document.createElement('div'));
    const portalParent$ = React.useRef<HTMLDivElement>();
    const { t } = useTranslation(['inspection-creation']);
    const navigate = useNavigate();
    const { asset, project } = useTypedSelector(state => ({
        asset: state.asset.changed,
        project: state.project,
    }));
    const [next, setNext] = React.useState<() => void>();
    const [steps, setSteps] = React.useState(0);
    const [step, setStep] = React.useState(0);
    const [unlocked, setUnlocked] = React.useState(false);

    React.useLayoutEffect(() => {
        schedulerParent$.current.classList.add(classes.schedulerParent);
        portalParent$.current?.append(schedulerParent$.current);
    }, [portalParent$.current]);

    if (!asset || !project.changed) return <span>{'#asset or project not in store'}</span>;

    return (
        <div className={classes.root}>
            {ReactDOM.createPortal(
                <ProjectScheduler
                    onStart={(next, steps) => (setNext(() => next), setSteps(steps))}
                    onStep={setStep}
                    onUnlocked={setUnlocked}
                    completeActions={[
                        { primary: true, name: t(`${mod}.scheduler-button-download`), onClick: () => {} },
                        { name: t(`${mod}.scheduler-button-home`), onClick: () => navigate('/') },
                    ]}
                />,
                schedulerParent$.current,
            )}
            {step < steps ? (
                <SectionCard
                    focused={true}
                    display={true}
                    header={
                        <SectionCardTitle
                            focused
                            message={t(`${mod}.scheduler-header-message`)}
                            title={t(`${mod}.scheduler-header-title`, { name: asset.name })}
                            subtitle={t(`${mod}.scheduler-header-subtitle`, { count: 1 })}
                        />
                    }
                >
                    <div ref={ref => (portalParent$.current = ref!)} className={classes.portalParent} />
                </SectionCard>
            ) : (
                <div ref={ref => (portalParent$.current = ref!)} className={classes.portalParent} />
            )}
            <div style={{ flex: 1, minHeight: 30 }} />
            <AnimatePresence>
                {step < steps && (
                    <motion.div className={classes.tracker} {...motions.presence({ slide: 10 })}>
                        <ProjectSchedulerTracker steps={steps} step={step} unlocked={unlocked} next={next} />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
